import { produce } from 'immer';
import { getTracker } from '../tracker';

const initialState = {
  tabs: [],
  selectedTab: '',
  sizeOptions: [],
  unitTypeOptions: [],
  selectedSize: {},
  selectedUnit: '',
  tableData: undefined,
  data: undefined,
  selectedTabIndex: 0,
  isLoading: false,
  isOptionsLoading: false,
  isLogHistoryLoading: false,
  acceptAll: false,
  acceptedItems: [],
  comments: {},
  showCommentsDialog: false,
  discounts: [],
  error: null,
  discountOptions: [],
  discountLogHistory: [],
  discountSuggestions: [],
};

// Adds N months to a date using a fixed 30-day month convention.
// Returns an ISO string for Redux serialization.
function addMonths(dateInput, months) {
  const base = new Date(dateInput);
  return new Date(base.getTime() + months * 30 * 24 * 60 * 60 * 1000).toISOString();
}

function getTableData(data) {
  let tabs = [];
  let sizeOptions = {};
  let unitTypeOptions = {};
  let tableData = {};
  let totalAccepted = {};
  let kpi = {};
  data.forEach((elem) => {
    if (tabs.indexOf(elem.Store) === -1) tabs.push(elem.Store);

    if (!tableData[elem.Store]) {
      tableData[elem.Store] = { data: [] };
      tableData[elem.Store].data.push(elem);

      unitTypeOptions[elem.Store] = {};
      sizeOptions[elem.Store] = {};
      totalAccepted[elem.Store] = {};
      kpi[elem.Store] = {};
    } else tableData[elem.Store].data.push(elem);

    if (!unitTypeOptions[elem.Store][elem.UnitType]) {
      unitTypeOptions[elem.Store][elem.UnitType] = 1;
      kpi[elem.Store][elem.UnitType] = {
        beekin: elem.RecommendPrice / elem.UnitSize,
        rentplus: elem.PreviousEffectivePrice / elem.UnitSize,
      };
    } else {
      unitTypeOptions[elem.Store][elem.UnitType]++;
      kpi[elem.Store][elem.UnitType].beekin +=
        elem.RecommendPrice / (elem.UnitSize * elem.UnitSize);
      kpi[elem.Store][elem.UnitType].rentplus +=
        elem.PreviousEffectivePrice / (elem.UnitSize * elem.UnitSize);
    }

    if (!sizeOptions[elem.Store][elem.UnitSize])
      sizeOptions[elem.Store][elem.UnitSize] = 1;
    else sizeOptions[elem.Store][elem.UnitSize]++;
  });

  tabs.sort();

  let unitOptions = {};
  let any = { text: 'Any', value: 'Any' };
  let selectedSize = {};
  Object.keys(unitTypeOptions).forEach((Store) => {
    unitOptions[Store] = Object.keys(unitTypeOptions[Store]).map((type) => {
      return { text: type, value: type, key: Store + type };
    });
    // sizeOpts[Store] = Object.keys(sizeOptions[Store]).map(size => {
    //     return { text: size, value: size };
    // })
    tableData[Store].unitOptions = unitOptions[Store];
    tableData[Store].unitOptions.splice(0, 0, {
      ...any,
      key: Store + 'unitany',
    });
    let obj = { Any: [] };

    tableData[Store].data.forEach((elem) => {
      if (
        obj[elem.UnitType] &&
        obj[elem.UnitType].indexOf(elem.UnitSize) === -1
      ) {
        obj[elem.UnitType].push(elem.UnitSize);
      } else {
        obj[elem.UnitType] = [];
        obj[elem.UnitType].push(elem.UnitSize);
      }
      if (obj.Any.indexOf(elem.UnitSize) === -1) obj.Any.push(elem.UnitSize);
    });
    selectedSize[Store] = {};
    Object.keys(obj).forEach((type) => {
      obj[type] = obj[type].map((size) => {
        return { text: size, value: size, key: Store + size };
      });
      obj[type].splice(0, 0, { ...any, key: Store + 'typeany' });
      selectedSize[Store][type] = '';
    });
    tableData[Store].sizeOptions = obj;
    tableData[Store].totalAccepted = totalAccepted[Store];
    let anyAccepted = 0;
    tableData[Store].accepted = {};
    tableData[Store].unitOptions.forEach((elem) => {
      anyAccepted +=
        elem.text !== 'Any' ? tableData[Store].totalAccepted[elem.text] : 0;
      tableData[Store].accepted[elem.text] = false;
      if (
        tableData[Store].totalAccepted[elem.text] ===
        tableData[Store].sizeOptions[elem.text].length - 1
      )
        tableData[Store].accepted[elem.text] = true;
    });
    if (anyAccepted === tableData[Store].data.length) {
      tableData[Store].accepted['Any'] = true;
    }
  });

  return { tableData, tabs, selectedSize, kpi };
}

function getModeledKpi(data) {
  let kpis = { chartData: {}, kpi: {} };
  data.chartData.forEach((d) => {
    if (!kpis.chartData[d.Store]) kpis.chartData[d.Store] = {};
    if (!kpis.chartData[d.Store][d.UnitType])
      kpis.chartData[d.Store][d.UnitType] = [d];
    else kpis.chartData[d.Store][d.UnitType].push(d);
  });
  data.kpis.forEach((d) => {
    if (!kpis.kpi[d.Store]) kpis.kpi[d.Store] = {};
    if (!kpis.kpi[d.Store][d.UnitType]) kpis.kpi[d.Store][d.UnitType] = d;
    // kpis.kpi[d.Store][d.UnitType].push(d);
  });
  return kpis;
}

const dashboardReducer = (state = initialState, action) => {
  switch (action.type) {
    case 'IS_LOADING': {
      return Object.assign({}, state, { isLoading: true });
    }
    case 'IS_NOT_LOADING': {
      return Object.assign({}, state, { isLoading: false });
    }
    case 'FETCH_DATA_FAILURE': {
      return { ...state, data: action.payload };
    }
    case 'FETCH_DATA_SUCCESS': {
      if (action.payload.priceCards.length > 0) {
        let { tableData, tabs, selectedSize } = getTableData(
          action.payload.priceCards
        );
        let kpis = getModeledKpi(action.payload.kpi);
        let { selectedTab, selectedUnit } = state;
        let tab = localStorage.getItem('selectedStore');
        selectedTab = tab && tabs.includes(tab) ? tab : tabs[0];

        selectedUnit = tableData[selectedTab].unitOptions[0].value; // unitTypeOptions[selectedTab][0].value;
        selectedSize[selectedTab][selectedUnit] =
          tableData[selectedTab].sizeOptions[selectedUnit][0].value; // sizeOptions[selectedTab][0].value;
        return {
          ...state,
          data: action.payload,
          isLoading: false,
          tableData,
          tabs,
          selectedSize,
          selectedTab,
          selectedUnit,
          selectedTabIndex: tabs.indexOf(selectedTab),
          kpiData: kpis, // { aws: action.payload.kpi, calculated: kpi, modeledKpi: undefined }
        };
      } else {
        return {
          ...state,
          data: action.payload,
          isLoading: false,
        };
      }
    }
    case 'UPDATE_SELECTED_UNIT': {
      return produce(state, (draft) => {
        getTracker().track(action.type, { selectedUnit: action.payload });

        const { selectedTab, tableData } = draft;
        const newSelectedUnit = action.payload;

        if (!draft.selectedSize[selectedTab]) {
          draft.selectedSize[selectedTab] = {};
        }
        if (!draft.selectedSize[selectedTab][newSelectedUnit]) {
          draft.selectedSize[selectedTab][newSelectedUnit] =
            tableData[selectedTab].sizeOptions[newSelectedUnit][0].value;
        }

        draft.selectedUnit = newSelectedUnit;
      });
    }

    case 'UPDATE_SELECTED_SIZE': {
      return produce(state, (draft) => {
        getTracker().track(action.type, { selectedSize: action.payload });

        const { selectedTab, selectedUnit } = draft;

        if (!draft.selectedSize[selectedTab]) {
          draft.selectedSize[selectedTab] = {};
        }
        draft.selectedSize[selectedTab][selectedUnit] = action.payload;
      });
    }

    case 'UPDATE_SELECTED_TAB': {
      return produce(state, (draft) => {
        getTracker().track(action.type, { selectedTab: action.payload });

        const { tableData } = draft;
        const selectedTab = action.payload;

        draft.selectedUnit = tableData[selectedTab].unitOptions[0].value;

        if (!draft.selectedSize[selectedTab]) {
          draft.selectedSize[selectedTab] = {};
        }
        if (!draft.selectedSize[selectedTab][draft.selectedUnit]) {
          draft.selectedSize[selectedTab][draft.selectedUnit] =
            tableData[selectedTab].sizeOptions[draft.selectedUnit][0].value;
        }

        localStorage.setItem('selectedStore', selectedTab);

        draft.selectedTab = selectedTab;
        draft.selectedTabIndex = draft.tabs.findIndex((e) => e === selectedTab);
      });
    }
    case 'UPDATE_STATUS': {
      return produce(state, (draft) => {
        const { selectedTab } = draft;
        const storeData = draft.tableData[selectedTab];

        if (
          action.payload.status === 'approved' ||
          action.payload.status === 'declared'
        ) {
          storeData.totalAccepted[action.payload.item.UnitType]++;
        } else {
          storeData.totalAccepted[action.payload.item.UnitType]--;
        }

        const index = storeData.data.findIndex(
          (elem) => elem.id === action.payload.item.id
        );
        if (index !== -1) storeData.data[index].status = action.payload.status;

        let total = 0;
        Object.keys(storeData.totalAccepted).forEach((v) => {
          total += storeData.totalAccepted[v];
        });
        storeData.accepted.Any = total === storeData.data.length;
      });
    }
    case 'UPDATE_STATUS_ALL': {
      return produce(state, (draft) => {
        const { Store, UnitType, status } = action.payload;
        const storeData = draft.tableData[Store];

        if (status === 'approved') {
          if (UnitType !== 'Any') {
            storeData.accepted[UnitType] = true;
            storeData.totalAccepted[UnitType] =
              storeData.sizeOptions[UnitType].length - 1;
            storeData.data.forEach((elem) => {
              if (elem.UnitType === UnitType) elem.status = status;
            });
          } else {
            Object.keys(storeData.totalAccepted).forEach((value) => {
              storeData.totalAccepted[value] =
                storeData.sizeOptions[value].length - 1;
              storeData.accepted[value] = true;
            });
            storeData.data.forEach((elem) => {
              elem.status = status;
            });
          }
        } else {
          if (UnitType !== 'Any') {
            storeData.accepted[UnitType] = false;
            storeData.data.forEach((elem) => {
              if (elem.UnitType === UnitType) elem.status = status;
            });
            storeData.totalAccepted[UnitType] = 0;
          } else {
            Object.keys(storeData.totalAccepted).forEach((value) => {
              storeData.totalAccepted[value] = 0;
              storeData.accepted[value] = false;
            });
            storeData.data.forEach((elem) => {
              elem.status = status;
            });
          }
        }

        let total = 0;
        Object.keys(storeData.totalAccepted).forEach((v) => {
          total += storeData.totalAccepted[v];
        });
        storeData.accepted.Any = total === storeData.data.length;

        draft.isLoading = false;
      });
    }

    case 'UPDATE_PRICE': {
      const { item, newPrice, comments, override } = action.payload;
      getTracker().track(action.type, action.payload);

      return produce(state, (draft) => {
        if (!draft.tableData || !draft.tableData[item.Store]) return;

        const storeData = draft.tableData[item.Store]?.data;

        for (let i = 0; i < storeData.length; i++) {
          if (parseInt(storeData[i].id) === parseInt(item.id)) {
            storeData[i].UpdatedPrice = newPrice;
            storeData[i].Price = newPrice;
            storeData[i].IsUpdated = true;
            storeData[i].comments = comments;

            if (override) {
              const { user } = action.payload;

              storeData[i].IsOverridden = true;
              storeData[i].OverriddenOn = new Date().toISOString();
              storeData[i].OverriddenBy = user;
            } else {
              storeData[i].IsOverridden = false;
            }
            break;
          }
        }

        draft.isLoading = false;
      });
    }

    case 'ADD_COMMENT': {
      return produce(state, (draft) => {
        const row = draft.tableData[action.payload.store]?.data.find(
          (e) => e.id === action.payload.priceId
        );
        if (row) row.totalcomments++;
      });
    }
    case 'NOTIFICATION_TYPE': {
      return produce(state, (draft) => {
        const { Type, data, Store } = action.payload;
        const storeData = draft.tableData[Store]?.data;
        if (!storeData) return;

        switch (Type) {
          case 'price_override': {
            const row = storeData.find((e) => e.id === '' + data.PriceId);
            if (row) {
              row.IsOverridden = true;
              row.OverriddenBy = data.override.CreatedBy;
              row.OverriddenOn = data.override.CreatedOn;
              row.Price = data.override.OverriddenPrice;
              row.UpdatedPrice = data.override.OverriddenPrice;
              row.IsUpdated = true;
            }
            break;
          }
          case 'price_update': {
            const row = storeData.find((e) => e.id === '' + data.PriceId);
            if (row) {
              row.IsOverridden = false;
              row.Price = data.NewPrice;
              row.UpdatedPrice = data.NewPrice;
              row.IsUpdated = true;
            }
            break;
          }
          case 'comment': {
            const row = storeData.find((e) => e.id === '' + data.PriceId);
            if (row) row.totalcomments++;
            break;
          }
          case 'alert': {
            break;
          }
          default: {
            break;
          }
        }
      });
    }
    case 'FREEZE_PRICE': {
      return produce(state, (draft) => {
        draft.tableData[action.payload.store].data.forEach((elem) => {
          elem.Price = elem.PreviousEffectivePrice;
          elem.UpdatedPrice = elem.PreviousEffectivePrice;
          elem.IsUpdated = true;
        });
      });
    }
    case 'FETCH_DISCOUNTS_SUCCESS': {
      return {
        ...state,
        discounts: action.payload,
        isLoading: false,
      };
    }
    case 'FETCH_DISCOUNTS_OPTIONS_REQUEST':
      return {
        ...state,
        isOptionsLoading: true,
        discountOptions: [],
      };
    case 'FETCH_DISCOUNT_SUGGESTIONS_SUCCESS':
      return {
        ...state,
        discountSuggestions: action.payload,
        isLoading: false,
      };
    case 'SUGGEST_DISCOUNT':
      return {
        ...state,
        discountSuggestions: [...state.discountSuggestions, action.payload],
      };
    case 'APPROVE_DISCOUNT_SUGGESTION_SUCCESS': {
      const _suggestionIndex = state.discountSuggestions.findIndex(
        (suggestion) => String(suggestion.id) === String(action.payload.id)
      );

      const updatedSuggestions =
        _suggestionIndex !== -1
          ? state.discountSuggestions.map((s, i) =>
              i === _suggestionIndex ? action.payload : s
            )
          : state.discountSuggestions;

      let updatedDiscounts = state.discounts;
      if (action.payload.ApprovedOn && state.discounts) {
        const duration = Number(action.payload.Duration) || 0;
        updatedDiscounts = state.discounts.map((discount) => {
          if (
            discount.StoreId === action.payload.StoreId &&
            discount.UnitSize === action.payload.UnitSize
          ) {
            return {
              ...discount,
              NewDiscountDealId: action.payload.NewDealId,
              NewDiscount: action.payload.NewDealTitle,
              IsOverridingAllowed: false,
              OverriddenOnDate: action.payload.ApprovedOn,
              OverrideTillDate: addMonths(action.payload.ApprovedOn, duration),
              OverriddenBy: action.payload.ApprovedBy,
            };
          }
          return discount;
        });
      }

      return {
        ...state,
        discounts: updatedDiscounts,
        discountSuggestions: updatedSuggestions,
        isLoading: false,
      };
    }

    case 'UPDATE_DISCOUNT_SUGGESTIONS': {
      const { suggestions } = action.payload;

      const suggestionsMap = new Map(
        suggestions.map((item) => [item.id, item])
      );

      const updatedSuggestions = (state.discountSuggestions || []).map(
        (item) => {
          const id = parseInt(item.id);
          return suggestionsMap.has(id) ? suggestionsMap.get(id) : item;
        }
      );

      const newSuggestions = suggestions.filter(
        (item) =>
          !(state.discountSuggestions || []).some(
            (existing) => parseInt(existing.id) === parseInt(item.id)
          )
      );

      return {
        ...state,
        discountSuggestions: [...updatedSuggestions, ...newSuggestions],
        isLoading: false,
      };
    }

    case 'UPDATE_DISCOUNT_OVERRIDES': {
      const { overrides } = action.payload;

      let updatedDiscounts = state.discounts;
      if (state.discounts) {
        updatedDiscounts = state.discounts.map((discount) => {
          const _override = overrides.find(
            (override) =>
              discount.UnitIdList.includes(Number(override.UnitId)) &&
              override.UnitSize === discount.UnitSize
          );

          if (_override) {
            const duration = Number(_override.Duration) || 0;
            return {
              ...discount,
              NewDiscountDealId: _override.DealId,
              NewDiscount: _override.UpdatedNewDiscount,
              IsOverridingAllowed: false,
              OverriddenOnDate: _override.CreatedOn,
              OverrideTillDate: addMonths(_override.CreatedOn, duration),
              OverriddenBy: _override.UserName,
            };
          }

          return discount;
        });
      }

      return {
        ...state,
        discounts: updatedDiscounts,
        isLoading: false,
      };
    }

    case 'FETCH_DISCOUNT_SUGGESTIONS_FAILED':
      return {
        ...state,
        discountSuggestions: [],
        isLoading: false,
        error: action.payload,
      };
    case 'FETCH_DISCOUNTS_OPTIONS_SUCCESS':
      return {
        ...state,
        isOptionsLoading: false,
        discountOptions: action.payload,
      };

    case 'FETCH_DISCOUNTS_OPTIONS_FAILURE':
      return {
        ...state,
        isOptionsLoading: false,
        error: action.payload,
      };

    case 'CLEAR_DISCOUNTS_OPTIONS':
      return {
        ...state,
        discountOptions: [],
        isOptionsLoading: false,
        error: null,
      };

    case 'FETCH_DISCOUNT_LOG_HISTORY_REQUEST': {
      return {
        ...state,
        isLogHistoryLoading: true,
      };
    }

    case 'FETCH_DISCOUNT_LOG_HISTORY_SUCCESS': {
      const flattend = Object.entries(action.payload).map(([date, logs]) => ({
        date,
        logs,
      }));
      return {
        ...state,
        discountLogHistory: flattend,
        isLogHistoryLoading: false,
      };
    }

    case 'FETCH_DISCOUNT_LOG_HISTORY_FAILURE': {
      return {
        ...state,
        discountLogHistory: [],
        isLogHistoryLoading: false,
        error: action.payload,
      };
    }

    case 'CLEAR_DISCOUNT_LOG_HISTORY':
      return {
        ...state,
        discountLogHistory: [],
        isLogHistoryLoading: false,
        error: null,
      };

    case 'UPDATE_DISCOUNT': {
      const { unitIds, duration, dealTitle, user } = action.payload;

      return produce(state, (draft) => {
        if (!draft.discounts?.length) return;

        const ids = String(unitIds).split(',').map(Number);
        const safeMonths = Number(duration) || 0;

        const overriddenOn = new Date().toISOString();
        const overrideTill = addMonths(overriddenOn, safeMonths);

        draft.discounts.forEach((row) => {
          const rowUnitIds = (row.UnitIdList || []).map(Number);

          if (rowUnitIds.some((id) => ids.includes(id))) {
            row.NewDiscount = dealTitle;
            row.IsOverridingAllowed = false;
            row.OverriddenOnDate = overriddenOn;
            row.OverrideTillDate = overrideTill;
            row.OverriddenBy = user;
          }
        });

        draft.isLoading = false;
      });
    }

    case 'LOGOUT': {
      return initialState;
    }

    default: {
      return state;
    }
  }
};
export default dashboardReducer;
